import React, { useState } from 'react';
import {
	Box,
	Card,
	CardContent,
	CardHeader,
	Collapse, List,
	Typography
} from '@mui/material';
import { InfoAvatar } from './InfoAvatar';
import { Expander } from './Expander';
import { ContactInfoMapper } from './ContactInfoMapper';
import { CourseItemImportantInfo } from './CourseItemImportantInfo';
import { CourseItemNotes } from './CourseItemNotes';
import { CourseTable } from './CourseTable';
import { isNextDay } from './helper/dateHelper';

export const CourseInfo = ({ courseData, color }) => {
	const [expanded, setExpanded] = useState(courseData.status !== 'abgesagt');
	const handleExpandClick = () => {
		setExpanded(!expanded);
	};
	const conferenceLeader = courseData.contact.conferenceLeader[0]?.fullName;
	const speaker = courseData.contact.speaker[0]?.fullName;
	const contentResponsible = courseData.contact.content[0]?.fullName;
	const eventResponsible = courseData.contact.event[0]?.fullName;

	return (
		<Card
			id='course-info-card'
			sx={{
				display: 'flex',
				flexDirection: 'column',
				borderRadius: '10px',
				boxShadow: 2,
				maxWidth: '100%',
			}}>
			<CardHeader
				sx={{
					alignItems: 'center',
					backgroundColor: expanded ? '#FBFCEA' : '#FFFFFF',
					padding: 0,
				}}
				avatar={<InfoAvatar identifier={'info'} />}
				title={
					<Typography sx={{
						color: color,
						'@container course-info (min-width: 0) and (max-width: 500px)': {
							fontSize: '1rem',
						},
						'@container course-info (min-width: 500px) and (max-width: 960px)': {
							fontSize: '1.375rem',
						},
						fontSize: '1.667rem',
						fontWeight: 'medium',
					}}>
						Wichtiges auf einen Blick
					</Typography>
				}
				action={<Expander expanded={expanded} onClick={handleExpandClick} color={color} />}
			/>
			<Collapse in={expanded}>
				<Box sx={{
					display: 'grid',
					'@container course-info (max-width: 960px)': {
						gridTemplateColumns: '100%',
						gridTemplateRows: 'repeat(3, min-content)',
						gridTemplateAreas: `
							"info"
							"notes"
							"contact"
						`,
					},
					gridTemplateColumns: '3.5fr 4.5fr',
					gridTemplateRows: 'min-content min-content',
					gridTemplateAreas: !courseData.importantInfo || courseData.importantInfo.length === 0 ? `
						"contact notes"
						 "contact notes"
					 ` : `
						"contact info"
						 "contact notes"
					 `,
				}}>
					<List sx={{ gridArea: 'contact', padding: '0 1rem 0 0' }}>
						{conferenceLeader ?
							<ContactInfoMapper array={courseData.contact.conferenceLeader} name='Tagungsleitung'
											   headline='Ihre Tagungsleitung' noContactInfo={true} />
							: speaker ?
								<ContactInfoMapper array={courseData.contact.speaker} name='Trainer*in'
												   headline='Ihre Trainer*innen' noContactInfo={true} />
								: null}
						<Typography sx={{
							padding: '1rem 0 0 1rem',
							fontSize: '1.222rem',
							fontWeight: 'medium',
							color: color
						}}>Ihre Ansprechpartner*innen</Typography>
						{eventResponsible &&
							<ContactInfoMapper array={courseData.contact.event} isSubheadline
											   name='organisatorisch verantwortlich'
											   headline='Für organisatorische Fragen wenden Sie sich bitte an:'
											   additionalSubhead='(Einladung, Anreise, Hotel usw.)'
											   noContactInfo={false} />
						}
						{contentResponsible &&
							<ContactInfoMapper array={courseData.contact.content} isSubheadline
											   name='inhaltlich verantwortlich'
											   headline='Für inhaltliche Fragen wenden Sie sich bitte an:'
											   noContactInfo={false} />
						}
					</List>
					<Box sx={{ p: '1rem' }}>
						{courseData.importantInfo && !isNextDay(courseData.validUntil) ?
							<CourseItemImportantInfo
								infoArray={courseData.importantInfo} gridArea='info' /> : null}
						{courseData.notes &&
							<CourseItemNotes
								notesArray={courseData.notes} gridArea='notes' />}
					</Box>
				</Box>
				<CardContent>
					<CourseTable courseData={courseData} />
				</CardContent>
			</Collapse>
		</Card>
	);
};